import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiPortalBackend, PortalWalletBalance } from '../../apis/ApiPortalBackend';
import { oauthClient } from '../../apis/OAuthClient';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { WALLET_REFRESH_EVENT } from '../../utils/walletRefresh';
// Die gelbe Coin ist der einzige warme Akzent der UI und bleibt dem Guthaben-Kontext
// vorbehalten (Dropdown/Billing); im Topbar laeuft die ruhige Theme-Variante.
import sngCoinsYellow from '../../assets/sengi_coin_yellow.png';
import sngCoinNavy from '../../assets/sengi_coin.png';
import sngCoinWhite from '../../assets/sengi_coin_white.png';

export function SengiCoinsWidget() {
  const [showDetails, setShowDetails] = useState(false);
  const [balance, setBalance] = useState<PortalWalletBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { effectiveTheme } = useBootstrapTheme();

  const loadBalance = useCallback(async () => {
    if (!oauthClient.getAccessToken()) return;
    setLoading(true);
    try {
      const result = await ApiPortalBackend.GetWallet();
      if (result) {
        setBalance(result.balance);
        setError(false);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalance();
    window.addEventListener(WALLET_REFRESH_EVENT, loadBalance);
    return () => window.removeEventListener(WALLET_REFRESH_EVENT, loadBalance);
  }, [loadBalance]);

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(num);

  const getBalanceColor = (total: number) => {
    if (total > 1000) return 'text-success';
    if (total > 100) return 'text-warning';
    return 'text-danger';
  };

  const total = balance?.total ?? 0;
  const meterSegment = (part: number) => total > 0 ? { width: `${(part / total) * 100}%` } : { width: '0%' };

  return (
    <div className="position-relative">
      <button
        className="btn btn-link text-body p-2 d-flex align-items-center text-decoration-none"
        onClick={() => { setShowDetails(!showDetails); if (!showDetails) loadBalance(); }}
      >
        <img src={effectiveTheme === 'dark' ? sngCoinWhite : sngCoinNavy} alt="Sengi Coins" style={{ height: '24px' }} className="me-2" />
        {loading && balance === null ? (
          <div className="spinner-border spinner-border-sm" role="status" style={{ width: '12px', height: '12px' }}>
            <span className="visually-hidden">Lade…</span>
          </div>
        ) : balance ? (
          <span className={`fw-bold small sengi-num ${getBalanceColor(balance.total)}`}>
            {formatNumber(balance.total)}
          </span>
        ) : (
          <span className="text-secondary small" title={error ? 'Kontostand konnte nicht geladen werden' : ''}>
            {error ? '—' : ''}
          </span>
        )}
      </button>

      {showDetails && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ zIndex: 1040 }}
            onClick={() => setShowDetails(false)}
          />
          <div
            className="position-absolute sengi-menu text-body"
            style={{ top: '100%', right: 0, width: '300px', zIndex: 1050, marginTop: '8px' }}
          >
            <div className="sengi-menu-header">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="sengi-menu-label">SengiCoins</span>
                <button
                  className="btn btn-link btn-sm p-0 text-secondary"
                  onClick={loadBalance}
                  disabled={loading}
                  title="Kontostand aktualisieren"
                >
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                </button>
              </div>

              {balance ? (
                <div className="d-flex align-items-center gap-2">
                  <div className="sengi-coin-badge"><img src={sngCoinsYellow} alt="" /></div>
                  <div className="flex-grow-1">
                    <div className={`fs-4 fw-bold sengi-num lh-1 ${getBalanceColor(balance.total)}`}>
                      {formatNumber(balance.total)}
                    </div>
                    <div className="sengi-meter mt-2" aria-hidden="true">
                      <div className="sengi-meter-sub" style={meterSegment(balance.subscriptionCoins)} />
                      <div className="sengi-meter-pre" style={meterSegment(balance.prepaidCoins)} />
                      <div className="sengi-meter-post" style={meterSegment(balance.postpaidCoins)} />
                    </div>
                    <div className="d-flex gap-3 mt-1 small text-secondary sengi-num">
                      <span><span className="sengi-dot sengi-dot-sub" />Abo {formatNumber(balance.subscriptionCoins)}</span>
                      <span><span className="sengi-dot sengi-dot-pre" />Prepaid {formatNumber(balance.prepaidCoins)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="small text-secondary">
                  {error ? 'Kontostand konnte nicht geladen werden.' : 'Lade…'}
                </div>
              )}
            </div>

            <div className="sengi-menu-divider" />

            <div className="px-2 pb-1">
              <button
                className="btn btn-sengi w-100"
                onClick={() => { setShowDetails(false); navigate('/billing'); }}
              >
                Credits aufladen
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
